import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/Login';
import AboutPage from './pages/About';
import PrivacyPage from './pages/Privacy';
import TermsPage from './pages/Terms';
import HomePage from './pages/Home';
import ListingsPage from './pages/Listings';
import ListingSellerActivityPage from './pages/ListingSellerActivity';
import TransactionsPage from './pages/Transactions';
import RentalsPage from './pages/Rentals';
import AgentsPage from './pages/Agents';
import MarketCentresPage from './pages/MarketCentres';
import TeamsPage from './pages/Teams';
import AssociatesPage from './pages/Associates';
import NotificationsPage from './pages/Notifications';
import ReportsPage from './pages/Reports';
import CMAGeneratorPage from './pages/CMAGenerator';
import AIToolsPage from './pages/AITools';
import LoomPage from './pages/Loom';
import TrainingHubPage from './pages/TrainingHub';
import MCAdminToolsPage from './pages/MCAdminTools';
import PortalLeadsPage from './pages/PortalLeads';
import LeadsContactsPage from './pages/LeadsContacts';
import { getAccessibleReports } from './pages/reportsConfig';
import { useAuth } from './contexts/AuthContext';
import { WebsiteStudioPreviewEntry } from './pages/WebsiteStudio';
import WebsiteStudio from './pages/WebsiteStudioDashboard';
import WebsiteStudioAuthenticated from './pages/WebsiteStudioAuthenticated';

function RootRedirect() {
  const { isOfficeAdmin, isRegionalAdmin } = useAuth();
  if (isOfficeAdmin || isRegionalAdmin) return <Navigate to="/mc-admin-tools" replace />;
  return <Navigate to="/home" replace />;
}

function ReportsRedirect() {
  const { isOfficeAdmin, isRegionalAdmin, isAgent } = useAuth();
  const accessibleReports = getAccessibleReports({ isOfficeAdmin, isRegionalAdmin, isAgent });
  if (accessibleReports.length === 0) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to={`/reports/${accessibleReports[0].id}`} replace />;
}

function RegionalAdminOnly({ children }: { children: JSX.Element }) {
  const { isRegionalAdmin } = useAuth();
  if (!isRegionalAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function RegionalAdminPilotOnly({ children }: { children: JSX.Element }) {
  const { isRegionalAdminAccount } = useAuth();
  if (!isRegionalAdminAccount) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {import.meta.env.DEV && import.meta.env.VITE_LAE_PREVIEW_ENABLED === 'true' && <Route path="/website-studio-preview" element={<WebsiteStudioPreviewEntry />} />}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/my-website" element={import.meta.env.DEV && import.meta.env.VITE_LAE_PREVIEW_ENABLED === 'true' ? <WebsiteStudio /> : <WebsiteStudioAuthenticated />} />
                <Route path="/listings" element={<ListingsPage />} />
                <Route path="/listings/:listingId/seller-activity" element={<ListingSellerActivityPage />} />
                <Route path="/rentals" element={<RentalsPage />} />
                <Route path="/transactions" element={<TransactionsPage />} />
                <Route path="/agents" element={<AgentsPage />} />
                <Route path="/market-centres" element={<MarketCentresPage />} />
                <Route path="/teams" element={<TeamsPage />} />
                <Route path="/associates" element={<AssociatesPage />} />
                <Route path="/listing-approvals" element={<Navigate to="/notifications" replace />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/reports" element={<ReportsRedirect />} />
                <Route path="/reports/:reportId" element={<ReportsPage />} />
                <Route path="/ai-tools" element={<AIToolsPage />} />
                <Route path="/cma" element={<CMAGeneratorPage />} />
                <Route path="/loom" element={<LoomPage />} />
                <Route path="/training-hub" element={<TrainingHubPage />} />
                <Route path="/mc-admin-tools" element={<MCAdminToolsPage />} />
                <Route path="/leads-contacts" element={<RegionalAdminPilotOnly><LeadsContactsPage /></RegionalAdminPilotOnly>} />
                <Route
                  path="/private-property-leads"
                  element={(
                    <RegionalAdminOnly>
                      <PortalLeadsPage />
                    </RegionalAdminOnly>
                  )}
                />
                <Route
                  path="/portal-leads"
                  element={(
                    <RegionalAdminOnly>
                      <PortalLeadsPage />
                    </RegionalAdminOnly>
                  )}
                />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
